import { useEffect, useState, type ReactNode } from "react";
import { useT } from "../i18n/hooks.ts";
import { TrashIcon } from "./TrashIcon.tsx";
import { IconButton } from "./IconButton.tsx";

/**
 * Shared "name + optional extra field(s) + delete button" header for a named-collection
 * editor's detail pane. Not used for UnitHydrographsEditor's separate reuse of the
 * `.named-collection-detail-header` CSS class on each month sub-section — that one has no
 * name/rename at all and doesn't fit this component's contract.
 */
export function NamedCollectionHeader({
  name,
  onRename,
  onDelete,
  deleteLabel,
  children,
}: {
  name: string;
  /** `useNamedCollection.ts`'s own `rename` — silently a no-op for a blank or already-used name, rather than saving it (the only guard against two items merging into one). */
  onRename: (value: string) => void;
  onDelete: () => void;
  deleteLabel: string;
  children?: ReactNode;
}) {
  const t = useT();
  // Buffered as text locally so a rejected in-progress value (blank while
  // clearing the field to retype, or briefly a duplicate while fixing a
  // typo) isn't wiped out by the controlled `name` prop before it becomes
  // valid again — `onRename` itself already silently no-ops for those, this
  // only stops that no-op from also fighting the user's own keystrokes.
  const [text, setText] = useState(name);
  useEffect(() => setText(name), [name]);
  return (
    <div className="named-collection-detail-header">
      <input
        type="text"
        className="named-collection-title-input"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onRename(e.target.value);
        }}
        aria-label={t("common.name")}
      />
      {children}
      <IconButton icon={<TrashIcon />} label={deleteLabel} onClick={onDelete} danger />
    </div>
  );
}
